// TVD-1301: SaaS self-serve entitlements + catalog + Razorpay billing APIs.
import api from "../utils/api";

const saasBase = (accountId) => `/accounts/${accountId}/saas`;

/**
 * GET /accounts/:accountId/saas/entitlements
 */
export const FetchSaasEntitlements = async (accountId) => {
  if (!accountId) {
    return { error: "accountId is required" };
  }
  try {
    const response = await api.get(`${saasBase(accountId)}/entitlements`, {
      suppressErrorToast: true,
    });
    if (response?.status === 200) {
      return response.data?.data || response.data;
    }
    return { error: "Unexpected response while fetching SaaS entitlements." };
  } catch (error) {
    return { error: error?.response?.data?.message || error?.message };
  }
};

/**
 * POST /accounts/:accountId/saas/onboarding-tour/complete
 * Marks the Free starter guided tour as done/dismissed. Replay does not clear this.
 */
export const CompleteSaasOnboardingTour = async (accountId) => {
  if (!accountId) {
    return { error: "accountId is required" };
  }
  try {
    const response = await api.post(
      `${saasBase(accountId)}/onboarding-tour/complete`,
      {},
      { suppressErrorToast: true }
    );
    if (response?.status === 200) {
      return response.data?.data || response.data;
    }
    return { error: "Failed to save guided tour progress." };
  } catch (error) {
    return { error: error?.response?.data?.message || error?.message };
  }
};

export const FetchSaasPacks = async (accountId) => {
  try {
    const response = await api.get(`${saasBase(accountId)}/catalog/packs`, {
      suppressErrorToast: true,
    });
    if (response?.status === 200) {
      return response.data?.data || response.data;
    }
    return { error: "Failed to load packs" };
  } catch (error) {
    return { error: error?.response?.data?.message || error?.message };
  }
};

export const QuoteSaasPack = async (accountId, { packId, amountInr, amount, promoCode } = {}) => {
  const resolvedAmount = amount != null ? amount : amountInr;
  const response = await api.post(`${saasBase(accountId)}/catalog/quote`, {
    ...(resolvedAmount != null ? { amount: Number(resolvedAmount) } : {}),
    ...(amountInr != null ? { amountInr: Number(amountInr) } : {}),
    ...(packId ? { packId } : {}),
    promoCode,
  });
  return response.data?.data || response.data;
};

export const CreateCheckoutIntent = async (
  accountId,
  { packId, amountInr, amount, promoCode, provider, billing } = {}
) => {
  const resolvedAmount = amount != null ? amount : amountInr;
  const response = await api.post(`${saasBase(accountId)}/checkout/create-intent`, {
    ...(resolvedAmount != null ? { amount: Number(resolvedAmount) } : {}),
    ...(amountInr != null ? { amountInr: Number(amountInr) } : {}),
    ...(packId ? { packId } : {}),
    promoCode,
    provider,
    billing,
  });
  return response.data?.data || response.data;
};

export const ConfirmCheckout = async (accountId, payload) => {
  const response = await api.post(`${saasBase(accountId)}/checkout/confirm`, payload);
  return response.data?.data || response.data;
};

/** @deprecated use CreateCheckoutIntent */
export const CreateRazorpayOrder = async (accountId, { packId, promoCode } = {}) => {
  const response = await api.post(`${saasBase(accountId)}/razorpay/create-order`, {
    packId,
    promoCode,
  });
  return response.data?.data || response.data;
};

/** @deprecated use ConfirmCheckout */
export const VerifyRazorpayPayment = async (accountId, payload) => {
  const response = await api.post(`${saasBase(accountId)}/razorpay/verify`, payload);
  return response.data?.data || response.data;
};

export const FetchSaasPayments = async (accountId, { status } = {}) => {
  if (!accountId) return [];
  try {
    const response = await api.get(`${saasBase(accountId)}/payments`, {
      params: status ? { status } : undefined,
      suppressErrorToast: true,
    });
    const data = response.data?.data || response.data || [];
    if (!Array.isArray(data)) return [];
    // Guard against Sequelize dataValues-only payloads from older API builds.
    return data.map((row) => (row?.dataValues ? { ...row, ...row.dataValues } : row));
  } catch (_error) {
    return [];
  }
};

export const FetchSaasPayment = async (accountId, paymentId) => {
  if (!accountId || !paymentId) return null;
  try {
    const response = await api.get(`${saasBase(accountId)}/payments/${paymentId}`, {
      suppressErrorToast: true,
    });
    const data = response.data?.data || response.data || null;
    if (!data) return null;
    return data.dataValues ? { ...data, ...data.dataValues } : data;
  } catch (_error) {
    return null;
  }
};

export const FetchSaasInvoice = async (accountId, paymentId) => {
  if (!accountId || !paymentId) return null;
  try {
    const response = await api.get(`${saasBase(accountId)}/payments/${paymentId}/invoice`, {
      suppressErrorToast: true,
    });
    return response.data?.data || response.data || null;
  } catch (_error) {
    return null;
  }
};

/**
 * Download Tax Invoice PDF for a successful payment (authenticated blob).
 * @returns {Promise<{ ok: boolean, error?: string }>}
 */
export const DownloadSaasInvoicePdf = async (accountId, paymentId) => {
  if (!accountId || !paymentId) {
    return { ok: false, error: 'accountId and paymentId are required' };
  }
  try {
    const response = await api.get(`${saasBase(accountId)}/payments/${paymentId}/invoice.pdf`, {
      responseType: 'blob',
      suppressErrorToast: true,
    });
    const contentType = String(response.headers?.['content-type'] || '');
    if (contentType.includes('application/json')) {
      return { ok: false, error: 'Invoice PDF is not ready yet.' };
    }
    const blob = response.data instanceof Blob
      ? response.data
      : new Blob([response.data], { type: 'application/pdf' });
    const disposition = String(response.headers?.['content-disposition'] || '');
    const match = disposition.match(/filename="?([^"]+)"?/i);
    const filename = match?.[1] || `invoice-${paymentId}.pdf`;
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    return { ok: true };
  } catch (error) {
    return {
      ok: false,
      error: error?.response?.data?.message || error?.message || 'Failed to download invoice PDF',
    };
  }
};

/**
 * SuperAdmin: GET /accounts/:accountId/saas/plan
 */
export const FetchSaasPlanOverview = async (accountId) => {
  if (!accountId) {
    return { error: 'accountId is required' };
  }
  try {
    const response = await api.get(`${saasBase(accountId)}/plan`, {
      suppressErrorToast: true,
    });
    if (response?.status === 200) {
      return response.data?.data || response.data;
    }
    return { error: 'Failed to load plan overview.' };
  } catch (error) {
    return { error: error?.response?.data?.message || error?.message };
  }
};

/**
 * SuperAdmin: PATCH /accounts/:accountId/saas/plan
 */
export const UpdateSaasPlan = async (accountId, { plan, note } = {}) => {
  if (!accountId || !plan) {
    return { error: 'accountId and plan are required' };
  }
  try {
    const response = await api.patch(`${saasBase(accountId)}/plan`, { plan, note });
    if (response?.status === 200) {
      const data = response.data?.data || response.data;
      return { ...data, changed: response.data?.changed };
    }
    return { error: 'Failed to update plan.' };
  } catch (error) {
    return { error: error?.response?.data?.message || error?.message };
  }
};

/**
 * GET /accounts/:accountId/saas/catalog/promo-hints
 */
export const FetchSaasPromoHints = async (accountId) => {
  if (!accountId) return { hints: [] };
  try {
    const response = await api.get(`${saasBase(accountId)}/catalog/promo-hints`, {
      suppressErrorToast: true,
    });
    if (response?.status === 200) {
      return response.data?.data || { hints: [] };
    }
    return { hints: [] };
  } catch (_error) {
    return { hints: [] };
  }
};

/**
 * SuperAdmin: GET /accounts/:accountId/saas/promo-codes
 */
export const FetchSaasPromoCodes = async (accountId) => {
  if (!accountId) {
    return { error: 'accountId is required' };
  }
  try {
    const response = await api.get(`${saasBase(accountId)}/promo-codes`, {
      suppressErrorToast: true,
    });
    if (response?.status === 200) {
      return response.data?.data || [];
    }
    return { error: 'Failed to load promo codes.' };
  } catch (error) {
    return { error: error?.response?.data?.message || error?.message };
  }
};

/**
 * SuperAdmin: POST /accounts/:accountId/saas/promo-codes
 */
export const CreateSaasPromoCode = async (accountId, payload) => {
  if (!accountId) {
    return { error: 'accountId is required' };
  }
  try {
    const response = await api.post(`${saasBase(accountId)}/promo-codes`, payload);
    if (response?.status === 201 || response?.status === 200) {
      return response.data?.data || response.data;
    }
    return { error: 'Failed to create promo code.' };
  } catch (error) {
    return { error: error?.response?.data?.message || error?.message };
  }
};

/**
 * SuperAdmin: PATCH /accounts/:accountId/saas/promo-codes/:id
 */
export const UpdateSaasPromoCode = async (accountId, promoCodeId, payload) => {
  if (!accountId || !promoCodeId) {
    return { error: 'accountId and promoCodeId are required' };
  }
  try {
    const response = await api.patch(`${saasBase(accountId)}/promo-codes/${promoCodeId}`, payload);
    if (response?.status === 200) {
      return response.data?.data || response.data;
    }
    return { error: 'Failed to update promo code.' };
  } catch (error) {
    return { error: error?.response?.data?.message || error?.message };
  }
};

/**
 * SuperAdmin: DELETE /accounts/:accountId/saas/promo-codes/:id (deactivate)
 */
export const DeactivateSaasPromoCode = async (accountId, promoCodeId) => {
  if (!accountId || !promoCodeId) {
    return { error: 'accountId and promoCodeId are required' };
  }
  try {
    const response = await api.delete(`${saasBase(accountId)}/promo-codes/${promoCodeId}`);
    if (response?.status === 200) {
      return response.data?.data || response.data;
    }
    return { error: 'Failed to deactivate promo code.' };
  } catch (error) {
    return { error: error?.response?.data?.message || error?.message };
  }
};

export const FetchSaasCreditLedger = async (accountId) => {
  if (!accountId) return [];
  try {
    const response = await api.get(`${saasBase(accountId)}/credit-ledger`, {
      suppressErrorToast: true,
    });
    const data = response.data?.data || response.data || [];
    if (!Array.isArray(data)) return [];
    return data.map((row) => (row?.dataValues ? { ...row, ...row.dataValues } : row));
  } catch (_error) {
    return [];
  }
};
